import { type FormEvent, useState } from "react";

import { api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { pantryCopy } from "../lib/i18n/pantry";
import { ScreenShell } from "./screen-shell";
import { Eyebrow, FormField, H1, Input, PrimaryButton, TopBar } from "./ui/primitives";

type Props = {
  householdId: string;
  onSaved: () => void;
  onCancel: () => void;
};

const CATEGORIES = [
  "nabiał",
  "mięso",
  "warzywa",
  "owoce",
  "pieczywo",
  "suche",
  "napoje",
  "mrożone",
  "słodycze",
  "przyprawy",
  "inne",
] as const;

const UNITS = ["g", "kg", "ml", "l", "szt", "opak", "łyż", "szkl"] as const;

type Unit = (typeof UNITS)[number];
type Category = (typeof CATEGORIES)[number];

// Shared inline style for native <select> to match the FormField/Input look.
const SELECT_STYLE: React.CSSProperties = {
  height: 44,
  padding: "0 14px",
  background: "var(--surface)",
  color: "var(--ink)",
  borderRadius: 10,
  boxShadow: "inset 0 0 0 1px var(--nav-border)",
  // 16px iOS auto-zoom guard — see primitives.tsx Input.
  fontSize: 16,
  appearance: "none",
  WebkitAppearance: "none",
  MozAppearance: "none",
};

export function PantryAddScreen({ householdId, onSaved, onCancel }: Props) {
  const { lang } = useLang();
  const t = pantryCopy[lang];
  const [name, setName] = useState("");
  const [quantity, setQuantity] = useState("");
  const [unit, setUnit] = useState<Unit>("szt");
  const [category, setCategory] = useState<Category>("inne");
  const [isStaple, setIsStaple] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.createPantryItem(householdId, {
        name: name.trim(),
        quantity: quantity ? Number(quantity) : null,
        unit,
        category,
        isStaple,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.errSave);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ScreenShell hideHeader>
      <TopBar back={t.addBack} title={t.addTopTitle} onBack={onCancel} />

      <div style={{ paddingTop: 18, paddingBottom: 8 }}>
        <Eyebrow>{t.addEyebrow}</Eyebrow>
        <H1 accent="." className="mt-3">
          {t.addTitle}
        </H1>
        <p style={{ marginTop: 12, fontSize: 14, color: "var(--ink-2)" }}>{t.addIntro}</p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3" style={{ marginTop: 6 }}>
        <FormField label={t.addFieldWhat}>
          <Input
            type="text"
            required
            maxLength={200}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t.addPlaceholderWhat}
          />
        </FormField>

        <div className="grid grid-cols-2 gap-2">
          <FormField label={t.addFieldQuantity}>
            <Input
              type="number"
              inputMode="decimal"
              min={0}
              step={0.1}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="2"
            />
          </FormField>
          <FormField label={t.addFieldUnit}>
            <select
              value={unit}
              onChange={(e) => setUnit(e.target.value as Unit)}
              className="outline-none transition-shadow"
              style={SELECT_STYLE}
            >
              {UNITS.map((u) => (
                <option key={u} value={u}>
                  {t.unitLabel[u]}
                </option>
              ))}
            </select>
          </FormField>
        </div>

        <FormField label={t.addFieldCategory}>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as Category)}
            className="outline-none transition-shadow"
            style={SELECT_STYLE}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {t.categoryLabel[c]}
              </option>
            ))}
          </select>
        </FormField>

        <label
          className="flex items-start gap-2 text-[13px] cursor-pointer p-3 rounded-lg"
          style={{ background: "var(--surface-2)", marginTop: 4 }}
        >
          <input
            type="checkbox"
            checked={isStaple}
            onChange={(e) => setIsStaple(e.target.checked)}
            className="w-4 h-4 mt-0.5 shrink-0 accent-[var(--accent)]"
          />
          <span style={{ color: "var(--ink-2)" }}>
            <strong style={{ color: "var(--ink)" }}>{t.addStapleStrong}</strong>
            {t.addStapleRest}
          </span>
        </label>

        {error ? (
          <p
            className="text-[12px] px-3 py-2 rounded-md"
            style={{ background: "var(--bad-bg)", color: "var(--bad-fg)" }}
          >
            {error}
          </p>
        ) : null}

        <PrimaryButton type="submit" disabled={submitting || !name.trim()} className="mt-2">
          {submitting ? t.saving : t.addSubmit}
        </PrimaryButton>

        <button
          type="button"
          onClick={onCancel}
          className="text-[13px] font-medium self-center"
          style={{ color: "var(--ink-2)", marginTop: 4 }}
        >
          {t.cancel}
        </button>
      </form>
    </ScreenShell>
  );
}
